import { creaAudio, erroreRichiesta, inviaPezzo, RiepilogoAudio, terminaAudio } from './verbaliAudioService';

// Caricamento di un file audio già registrato: pezzi da 8 MiB (l'ultimo più corto) con ripresa.
// La stessa impronta (nome|dimensione|ultima modifica) ritrova sul server il caricamento
// interrotto e si inviano solo i pezzi mancanti; poi «Termina» lo mette in coda di trascrizione.

export const DIMENSIONE_PEZZO = 8 * 1024 * 1024;
export const MAX_DIMENSIONE_FILE = 1024 ** 3;
export const TENTATIVI_PER_PEZZO = 3;
export const ATTESE_CARICAMENTO_MS = [2000, 5000];

export const ERRORE_FILE_TROPPO_GRANDE = 'Il file supera 1 GB: esportalo in mp3 o m4a e riprova';
export const ERRORE_FILE_VUOTO = 'Il file è vuoto: scegli un altro file audio';
const ERRORE_CONNESSIONE = 'Caricamento interrotto: controlla la connessione e riprova, ripartirà da dove si è fermato';

export interface OpzioniCaricamento {
  eventId: string;
  file: File;
  avvisoConfermato: boolean;
  onAvanzamento: (inviati: number, totale: number) => void;
  segnale?: AbortSignal;
  /** Attesa tra i tentativi (iniettabile nei test). */
  attendi?: (ms: number) => Promise<void>;
}

const erroreInterrotto = () => {
  const e = new Error('Caricamento annullato');
  e.name = 'AbortError';
  return e;
};

const attesaPredefinita = (ms: number, segnale?: AbortSignal) => new Promise<void>(resolve => {
  const timer = setTimeout(fine, ms);
  function fine() {
    clearTimeout(timer);
    segnale?.removeEventListener('abort', fine);
    resolve();
  }
  segnale?.addEventListener('abort', fine);
});

/** Rete = errore di axios senza risposta; altre eccezioni senza risposta non si riprovano. */
const diRete = (err: any) => !!err?.isAxiosError && !err.response;

function riprovabile(err: any): boolean {
  if (diRete(err)) return true;
  const { status } = erroreRichiesta(err);
  return status !== null && (status === 408 || status === 429 || status >= 500);
}

/** Errore leggibile con il messaggio del server (status e codice in proprietà). */
function erroreDa(err: any): Error {
  const d = erroreRichiesta(err);
  let messaggio = d.messaggio;
  if (diRete(err)) messaggio = ERRORE_CONNESSIONE;
  else if (d.status === null) messaggio = (typeof err?.message === 'string' && err.message) || 'Errore imprevisto nel caricamento';
  return Object.assign(new Error(messaggio), { status: d.status, codice: d.codice });
}

export async function caricaFileAudio({
  eventId,
  file,
  avvisoConfermato,
  onAvanzamento,
  segnale,
  attendi,
}: OpzioniCaricamento): Promise<RiepilogoAudio> {
  if (file.size > MAX_DIMENSIONE_FILE) throw new Error(ERRORE_FILE_TROPPO_GRANDE);
  if (file.size <= 0) throw new Error(ERRORE_FILE_VUOTO);
  if (segnale?.aborted) throw erroreInterrotto();
  const aspetta = attendi || ((ms: number) => attesaPredefinita(ms, segnale));

  let creato;
  try {
    creato = await creaAudio(eventId, {
      origine: 'file',
      avvisoConfermato,
      nomeOriginale: file.name,
      dimensione: file.size,
      impronta: `${file.name}|${file.size}|${file.lastModified}`,
      ...(file.type ? { mime: file.type } : {}),
    });
  } catch (err) {
    throw erroreDa(err);
  }
  const audioId = creato.audio._id;
  const totale = Math.ceil(file.size / DIMENSIONE_PEZZO);
  const ricevuti = new Set(creato.pezziRicevuti.filter(i => Number.isInteger(i) && i >= 0 && i < totale));
  let inviati = ricevuti.size;
  if (inviati) onAvanzamento(inviati, totale);

  for (let indice = 0; indice < totale; indice++) {
    if (ricevuti.has(indice)) continue;
    const blob = file.slice(indice * DIMENSIONE_PEZZO, Math.min((indice + 1) * DIMENSIONE_PEZZO, file.size));
    for (let tentativo = 1; ; tentativo++) {
      if (segnale?.aborted) throw erroreInterrotto();
      try {
        await inviaPezzo(eventId, audioId, 0, indice, blob, segnale);
        break;
      } catch (err) {
        if (segnale?.aborted) throw erroreInterrotto();
        if (!riprovabile(err) || tentativo >= TENTATIVI_PER_PEZZO) throw erroreDa(err);
        await aspetta(ATTESE_CARICAMENTO_MS[Math.min(tentativo - 1, ATTESE_CARICAMENTO_MS.length - 1)]);
      }
    }
    inviati += 1;
    onAvanzamento(inviati, totale);
  }

  if (segnale?.aborted) throw erroreInterrotto();
  try {
    const { audio } = await terminaAudio(eventId, audioId, {});
    return audio;
  } catch (err) {
    throw erroreDa(err);
  }
}
